'use client';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { CollapseArrowDownOutlined, CollapseArrowUpOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import React, { useContext, useState } from 'react';

// FAQ数据类型
interface FaqItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FaqSectionProps {
  className?: string;
}

export default function FaqSection({ className = '' }: FaqSectionProps) {
  const t = useTranslations('track-order.faq');
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const [expandedItems, setExpandedItems] = useState<string[]>(['1']);

  // FAQ静态数据 - 从翻译获取
  const faqData: FaqItem[] = [
    {
      id: '1',
      question: t('q1'),
      answer: t.rich('a1', {
        link: (chunks) => (
          <a href="/contact-zeelool" style={{ color: '#463AE8', textDecoration: 'underline' }}>
            {chunks}
          </a>
        ),
      }),
    },
    {
      id: '2',
      question: t('q2'),
      answer: t.rich('a2', {
        link: (chunks) => (
          <a href="/contact-zeelool" style={{ color: '#463AE8', textDecoration: 'underline' }}>
            {chunks}
          </a>
        ),
      }),
    },
    {
      id: '3',
      question: t('q3'),
      answer: t('a3'),
    },
    {
      id: '4',
      question: t('q4'),
      answer: t('a4'),
    },
  ];

  // 处理展开/收起
  const toggleExpanded = (id: string) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // PC端布局
  const renderPCLayout = () => {
    return (
      <div className="faq-wrap w-full max-w-[930px] mx-auto py-[60px]">
        {/* 标题 */}
        <h2 className="text-center text-[24px] font-bb mb-[30px] text-[#333]">{t('title')}</h2>

        {/* FAQ列表 */}
        <ul className="space-y-[32px]">
          {faqData.map((item) => (
            <li key={item.id}>
              {/* 问题 */}
              <div className="question mb-[12px] text-[16px] font-bb text-[#333]">
                <span className="text-primary-purple mr-[8px]">{t('Q')}</span>
                {item.question}
              </div>

              {/* 答案 */}
              <div className="answer pl-[22px] text-[12px] leading-[18px] text-[#666] font-br">
                {item.answer}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // 移动端布局（手风琴式）
  const renderMobileLayout = () => {
    return (
      <div className="faq-wrap w-full px-[14px] py-[40px] bg-white">
        {/* 标题 */}
        <h2 className="text-center text-[16px] font-bb mb-[32px] text-[#333]">{t('title')}</h2>

        {/* FAQ列表 */}
        <div className="space-y-[16px]">
          {faqData.map((item, index) => {
            const isExpanded = expandedItems.includes(item.id);

            return (
              <div
                key={item.id}
                className={`border-t border-[#d4d4d4] ${index === faqData.length - 1 ? 'border-b' : ''}`}
              >
                {/* 问题 - 可点击展开 */}
                <div
                  className="question flex items-center justify-between my-[18px] cursor-pointer"
                  onClick={() => toggleExpanded(item.id)}
                >
                  <div className="flex-1 text-[14px] font-bb text-[#333] text-left">
                    <span className="text-primary-purple mr-[8px]">{t('Q')}</span>
                    {item.question}
                  </div>
                  <div className="ml-[16px] text-[20px] text-[#666] transition-transform duration-300">
                    {isExpanded ? <CollapseArrowUpOutlined /> : <CollapseArrowDownOutlined />}
                  </div>
                </div>

                {/* 答案 - 可展开收起 */}
                {isExpanded && (
                  <div className="answer pb-[24px] pl-[22px] pr-[40px]">
                    <div className="text-[12px] leading-[20px] text-[#666] font-br">
                      {item.answer}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <section className={`faq-section ${className}`}>
      {isWeb ? renderPCLayout() : renderMobileLayout()}
    </section>
  );
}
